import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BookProvider } from "@/context/BookContext";
import { Sidebar } from "@/components/layout/Shell";
import { Spin } from "@/components/common";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Books from "@/pages/Books";
import NewBook from "@/pages/NewBook";
import BookManage from "@/pages/BookManage";
import PageEditor from "@/pages/PageEditor";
import AIEdit from "@/pages/AIEdit";
import PageImage from "@/pages/PageImage";
import Typesetting from "@/pages/Typesetting";
import CharacterStudio from "@/pages/CharacterStudio";
import CharacterCreate from "@/pages/CharacterCreate";
import Cover from "@/pages/Cover";
import Questions from "@/pages/Questions";
import Film from "@/pages/Film";
import Assets from "@/pages/Assets";
import Revisions from "@/pages/Revisions";
import ScenePrompt from "@/pages/ScenePrompt";
import Delivery from "@/pages/Delivery";
import Acceptance from "@/pages/Acceptance";
import SystemSettings from "@/pages/SystemSettings";
import ApiConnections from "@/pages/ApiConnections";
import SeriesShelf, { PublicShelf } from "@/pages/SeriesShelf";
import PromptTemplates from "@/pages/PromptTemplates";
import DataModel from "@/pages/DataModel";
import DevDoc from "@/pages/DevDoc";
import Preview from "@/pages/Preview";
import Publish from "@/pages/Publish";
import FilmGallery from "@/pages/FilmGallery";
import SharePublic from "@/pages/SharePublic";
import AuthorStudio from "@/pages/AuthorStudio";
import WorkReader from "@/pages/WorkReader";
import MathLevels from "@/pages/math/MathLevels";
import MathPublications from "@/pages/math/MathPublications";
import MathPool from "@/pages/math/MathPool";
import MathQuestionEditor from "@/pages/math/MathQuestionEditor";
import MathWorkshop from "@/pages/math/MathWorkshop";
import MathTemplates from "@/pages/math/MathTemplates";
import MathGeometry from "@/pages/math/MathGeometry";
import MathDesignStudio from "@/pages/math/MathDesignStudio";
import MathExports from "@/pages/math/MathExports";

function Protected() {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="h-screen grid place-items-center text-teal"><Spin size={28} /></div>;
  if (!user) return <Navigate to="/giris" replace />;
  return (
    <BookProvider>
      <div className="flex min-h-screen bg-[var(--cream)]">
        {!loc.pathname.startsWith("/matematik") && <Sidebar />}
        <Outlet />
      </div>
    </BookProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/giris" element={<Login />} />
          <Route path="/paylas/:token" element={<SharePublic />} />
          <Route path="/raf/:token" element={<PublicShelf />} />
          <Route element={<Protected />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/kitaplar" element={<Books />} />
            <Route path="/kitaplar/yeni" element={<NewBook />} />
            <Route path="/kitaplar/:id" element={<BookManage />} />
            <Route path="/sayfa-editoru" element={<PageEditor />} />
            <Route path="/ai-duzenleme" element={<AIEdit />} />
            <Route path="/sayfa-gorseli" element={<PageImage />} />
            <Route path="/dizgi" element={<Typesetting />} />
            <Route path="/karakterler" element={<CharacterStudio />} />
            <Route path="/karakterler/yeni" element={<CharacterCreate />} />
            <Route path="/karakterler/:cid/duzenle" element={<CharacterCreate />} />
            <Route path="/kapak" element={<Cover />} />
            <Route path="/sorular" element={<Questions />} />
            <Route path="/film" element={<Film />} />
            <Route path="/varliklar" element={<Assets />} />
            <Route path="/surumler" element={<Revisions />} />
            <Route path="/sahne-komutu" element={<ScenePrompt />} />
            <Route path="/teslim-takibi" element={<Delivery />} />
            <Route path="/kabul-testleri" element={<Acceptance />} />
            <Route path="/ayarlar" element={<SystemSettings />} />
            <Route path="/ayarlar/api" element={<ApiConnections />} />
            <Route path="/ayarlar/komut-sablonlari" element={<PromptTemplates />} />
            <Route path="/ayarlar/veri-modeli" element={<DataModel />} />
            <Route path="/ayarlar/gelistirme" element={<DevDoc />} />
            <Route path="/onizleme" element={<Preview />} />
            <Route path="/baski" element={<Publish />} />
            <Route path="/filmler" element={<FilmGallery />} />
            <Route path="/seri-rafi" element={<SeriesShelf />} />
            <Route path="/yazar" element={<AuthorStudio />} />
            <Route path="/yazar/:wid" element={<WorkReader />} />
            <Route path="/matematik" element={<MathLevels />} />
            <Route path="/matematik/yayinlar" element={<MathPublications />} />
            <Route path="/matematik/havuz/:kind" element={<MathPool />} />
            <Route path="/matematik/soru/:iid" element={<MathQuestionEditor />} />
            <Route path="/matematik/atolye" element={<MathWorkshop />} />
            <Route path="/matematik/kitap/:bid" element={<MathWorkshop />} />
            <Route path="/matematik/sablonlar" element={<MathTemplates />} />
            <Route path="/matematik/geometri" element={<MathGeometry />} />
            <Route path="/matematik/tasarim" element={<MathDesignStudio />} />
            <Route path="/matematik/ciktilar" element={<MathExports />} />
          </Route>
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors offset={72} />
    </AuthProvider>
  );
}
